/**
 * Configuração centralizada do Sentry para monitoramento de erros
 * 
 * RELEASE: 2026-01-27
 * VERSION: 1.0.0
 * 
 * O Sentry captura:
 * - Erros de JavaScript/TypeScript não tratados
 * - Erros de componentes React (via ErrorBoundary)
 * - Erros logados via logger centralizado
 * - Performance traces (amostragem de 10%)
 * - Session replays em caso de erro (100%)
 */

import * as Sentry from '@sentry/react';

/**
 * Inicializa o Sentry com configurações otimizadas para produção
 * 
 * Deve ser chamado antes de qualquer renderização React (em main.tsx)
 * 
 * Configurações:
 * - DSN: Chave pública do projeto Sentry (publishable key)
 * - Environment: Detectado automaticamente (production/development)
 * - Traces: 10% das transações são amostradas
 * - Replays: 100% das sessões com erro são gravadas
 */
export const initSentry = (): void => {
  // Só inicializa em produção para evitar ruído em desenvolvimento
  if (import.meta.env.PROD) {
    const dsn = import.meta.env.VITE_SENTRY_DSN;
    
    // Se DSN não estiver configurado, não inicializa (fail-safe)
    if (!dsn) {
      console.warn('[Sentry] DSN não configurado - monitoramento desabilitado');
      return;
    }

    Sentry.init({
      dsn,
      environment: 'production',
      
      // Integrações habilitadas
      integrations: [
        // Rastreamento de performance (navegação, requisições, etc.)
        Sentry.browserTracingIntegration(),
        // Gravação de sessão para replay em caso de erro
        Sentry.replayIntegration({
          // Mascara inputs sensíveis automaticamente
          maskAllText: false,
          maskAllInputs: true,
          blockAllMedia: false,
        }),
      ],
      
      // Amostragem de traces de performance (10%)
      // Reduz volume sem perder visibilidade de problemas
      tracesSampleRate: 0.1,
      
      // Gravação de replay em caso de erro (100%)
      // Permite reproduzir exatamente o que o usuário fez antes do erro
      replaysOnErrorSampleRate: 1.0,
      
      // Replay para sessões normais (1%)
      // Amostra para análise de UX sem impacto em performance
      replaysSessionSampleRate: 0.01,
      
      // Ignora erros comuns que não são acionáveis
      ignoreErrors: [
        // Erros de rede que são esperados (offline, timeout)
        'Network request failed',
        'Failed to fetch',
        'Load failed',
        // Erros de extensões de navegador
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
        // Erros de scripts de terceiros
        'Script error.',
      ],
      
      // Antes de enviar, adiciona contexto extra
      beforeSend(event, hint) {
        // Adiciona informações do tenant se disponível
        const tenantId = localStorage.getItem('activeTenantId');
        if (tenantId) {
          event.tags = {
            ...event.tags,
            tenant_id: tenantId.substring(0, 8).toUpperCase(), // Mascarado
          };
        }
        
        return event;
      },
    });
    
    console.info('[Sentry] Monitoramento inicializado');
  }
};

/**
 * Configura contexto do usuário no Sentry
 * Deve ser chamado após login bem-sucedido
 * 
 * @param userId - UUID do usuário (será mascarado)
 * @param email - Email do usuário
 * @param role - Role do usuário (agent, admin, etc.)
 */
export const setSentryUser = (
  userId: string | null,
  email?: string,
  role?: string
): void => {
  if (import.meta.env.PROD && userId) {
    Sentry.setUser({
      id: userId.substring(0, 8).toUpperCase(), // Mascarado para privacidade
      email,
      role,
    });
  }
};

/**
 * Remove contexto do usuário (após logout)
 */
export const clearSentryUser = (): void => {
  if (import.meta.env.PROD) {
    Sentry.setUser(null);
  }
};

/**
 * Captura exceção manualmente com contexto adicional
 * Útil para erros tratados que ainda queremos monitorar
 * 
 * @param error - Erro a ser capturado
 * @param context - Contexto adicional (tags, extras)
 */
export const captureException = (
  error: Error | unknown,
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
    level?: Sentry.SeverityLevel;
  }
): void => {
  if (import.meta.env.PROD) {
    Sentry.captureException(error, {
      tags: context?.tags,
      extra: context?.extra,
      level: context?.level || 'error',
    });
  }
};

/**
 * Captura mensagem manualmente (não é uma exceção)
 * Útil para alertas de negócio ou situações inesperadas
 * 
 * @param message - Mensagem a ser capturada
 * @param level - Nível de severidade
 * @param context - Contexto adicional
 */
export const captureMessage = (
  message: string,
  level: Sentry.SeverityLevel = 'info',
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
  }
): void => {
  if (import.meta.env.PROD) {
    Sentry.captureMessage(message, {
      level,
      tags: context?.tags,
      extra: context?.extra,
    });
  }
};

// Re-exporta ErrorBoundary do Sentry para uso direto
export const SentryErrorBoundary = Sentry.ErrorBoundary;

// Exporta Sentry para usos avançados
export { Sentry };
